import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Ban, Check, Copy, Download, ExternalLink, FileSignature, FileText, Link as LinkIcon, Mail, PenLine, Plus, Save, Search, Settings2, Trash2, UserCheck, X } from 'lucide-react';
import PdfCanvasViewer from '../components/PdfCanvasViewer';
import AuditTrail from '../components/AuditTrail';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';
import { getListStatus } from '../lib/statusModel';
import { lastUpdatedTimestamp } from '../lib/timestamps';

const fmt = (v) => (v == null || v === '' ? '-' : v);

const badgeStyles = {
  draft: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  sent: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  opened: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  signing_started: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  created: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  setup_required: 'bg-amber-50 text-amber-800 border-amber-300',
  ready_to_send: 'bg-amber-50 text-amber-800 border-amber-300',
  pending_verification: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  ready_to_sign: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  expired: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
};

const verificationBadgeStyles = {
  required: 'bg-white text-zinc-700 border-zinc-200',
  verified: 'bg-white text-zinc-700 border-zinc-200',
  failed: 'bg-white text-zinc-700 border-zinc-200',
};

const terminalDocumentStatuses = new Set(['revoked', 'expired', 'rejected', 'failed']);

function isTerminalDocumentStatus(status) {
  return terminalDocumentStatuses.has(String(status || '').toLowerCase());
}

function normalizeChecklistTone({ complete = false, active = false, terminal = false, failed = false } = {}) {
  if (complete) return 'success';
  if (failed || (terminal && !complete)) return 'error';
  if (active) return 'neutral';
  return 'inactive';
}

function mainWorkflowTone(tone) {
  return tone === 'neutral' ? 'pending' : tone;
}

function SignatureBadge({ status, displayStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${badgeStyles[status] || badgeStyles.draft}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {displayStatus || String(status || 'draft').replaceAll('_', ' ')}
    </span>
  );
}

function verificationBadgeForSignature(signature) {
  if (!(signature.auth_method === 'kyc' || signature.signature_level === 'AES') || !signature.verification_session_id) return null;
  if (signature.verification_status === 'approved') {
    return { label: 'KYC approved', tone: 'verified' };
  }
  if (signature.verification_status === 'rejected') {
    return { label: 'KYC rejected', tone: 'failed' };
  }
  if (signature.verification_status === 'revoked') {
    return { label: 'KYC revoked', tone: 'failed' };
  }
  if (signature.verification_status === 'expired') {
    return { label: 'KYC expired', tone: 'failed' };
  }
  return { label: 'KYC pending', tone: 'required' };
}

function LinkedVerificationBadge({ badge, verificationId }) {
  if (!badge) return null;
  const className = `inline-flex items-center gap-1.5 px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border hover:border-zinc-400 hover:bg-zinc-50 ${verificationBadgeStyles[badge.tone] || verificationBadgeStyles.required}`;
  if (!verificationId) {
    return (
      <span className={className}>
        <LinkIcon className="w-3 h-3" strokeWidth={1.5}/>
        {badge.label}
      </span>
    );
  }

  return (
    <Link to={`/dashboard/verifications/${verificationId}`} className={className}>
      <LinkIcon className="w-3 h-3" strokeWidth={1.5}/>
      {badge.label}
    </Link>
  );
}

function hasValue(value) {
  if (React.isValidElement(value)) return true;
  return value !== null && value !== undefined && value !== '';
}

function Field({ label, value, mono, hideEmpty = false, noDivider = false }) {
  if (hideEmpty && !hasValue(value)) return null;
  return (
    <div className={`grid grid-cols-1 gap-1 py-3 ${noDivider ? '' : 'border-b border-zinc-100 last:border-0'} sm:grid-cols-[minmax(160px,176px)_minmax(0,1fr)] sm:gap-4`}>
      <div className="min-w-0 text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`min-w-0 break-words ${mono ? 'font-mono text-sm' : 'text-sm'}`}>{fmt(value)}</div>
    </div>
  );
}

const iconStateStyles = {
  success: 'bg-green-50 text-green-700 border-green-300',
  error: 'bg-red-50 text-red-700 border-red-300',
  pending: 'bg-zinc-50 text-zinc-500 border-zinc-300',
  inactive: 'bg-zinc-50 text-zinc-500 border-zinc-300',
  neutral: 'bg-zinc-50 text-zinc-500 border-zinc-300',
};

function IconState({ tone, shape = 'round' }) {
  const resolvedTone = tone || 'neutral';
  const shapeClass = shape === 'square' ? 'rounded-sm h-7 w-7' : 'rounded-full h-6 w-6';
  return (
    <span className={`inline-flex shrink-0 items-center justify-center border ${shapeClass} ${iconStateStyles[resolvedTone] || iconStateStyles.neutral}`}>
      {resolvedTone === 'success' && <Check className="w-3 h-3" strokeWidth={2}/>}
      {resolvedTone === 'error' && <X className="w-3 h-3" strokeWidth={2}/>}
      {resolvedTone === 'pending' && <span className="h-3 w-3 rounded-full border border-current border-t-transparent opacity-70 animate-spin"/>}
      {resolvedTone === 'inactive' && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>}
      {resolvedTone === 'neutral' && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>}
    </span>
  );
}

function WorkflowStepCard({ label, description, tone, shape = 'round', icon: Icon, variant = 'primary', linked = false }) {
  const isSubcheck = variant === 'subcheck';
  const cardStyle = isSubcheck
    ? 'rounded-full border-zinc-200 bg-white px-3 py-2'
    : 'rounded-md border-zinc-200 bg-white px-3 py-2.5';
  const borderStyle = linked ? 'border-dashed' : 'border-solid';
  return (
    <div className={`min-w-0 border ${borderStyle} ${cardStyle}`}>
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-700 ${isSubcheck ? 'font-normal' : 'font-semibold'}`}>
              {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.7}/>}
              {label}
            </span>
          </div>
          {description && <div className="mt-1.5 text-xs text-zinc-500 leading-relaxed">{description}</div>}
        </div>
        <div className="shrink-0">
          <IconState tone={tone} shape={shape}/>
        </div>
      </div>
    </div>
  );
}

function WorkflowBranchRow({ children }) {
  return (
    <div className="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)] items-center gap-0">
      <span aria-hidden="true" className="h-px bg-zinc-200/90"/>
      <div className="min-w-0 w-full sm:w-[88%]">{children}</div>
    </div>
  );
}

function WorkflowSubsteps({ substeps, connectsToNext = false }) {
  if (!substeps?.length) return null;
  return (
    <div className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-0">
      <div className="flex justify-end">
        <span aria-hidden="true" className="w-px bg-zinc-200/90"/>
      </div>
      <div className={`min-w-0 pt-3 ${connectsToNext ? 'pb-0' : 'pb-3'}`}>
        <div className="space-y-3">
          {substeps.map((step) => (
            <WorkflowBranchRow key={step.key}>
              <WorkflowStepCard {...step} shape="round" variant="subcheck"/>
            </WorkflowBranchRow>
          ))}
        </div>
      </div>
    </div>
  );
}

function WorkflowConnectorSegment() {
  return (
    <div className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-0">
      <div className="flex justify-end">
        <span aria-hidden="true" className="h-3 w-px bg-zinc-200/90"/>
      </div>
      <div aria-hidden="true"/>
    </div>
  );
}

function DocumentWorkflowCard({ steps }) {
  return (
    <section className="border border-zinc-200 rounded-sm bg-white p-6">
      <h2 className="font-display text-lg font-semibold">Workflow</h2>
      <p className="text-sm text-zinc-500 mt-0.5">Current document workflow status.</p>
      <div className="mt-5">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 && <WorkflowConnectorSegment/>}
            <WorkflowStepCard {...step} shape="square"/>
            <WorkflowSubsteps substeps={step.substeps} connectsToNext={index < steps.length - 1}/>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

const documentNavigatorStatus = {
  signed: { label: 'Signed', tone: 'border-emerald-300 bg-emerald-50 text-emerald-700', icon: 'check' },
  completed: { label: 'Signed', tone: 'border-emerald-300 bg-emerald-50 text-emerald-700', icon: 'check' },
  action_required: { label: 'Action required', tone: 'border-amber-300 bg-amber-50 text-amber-800', icon: 'dot' },
  failed: { label: 'Failed', tone: 'border-red-300 bg-red-50 text-red-700', icon: 'x' },
  expired: { label: 'Expired', tone: 'border-red-300 bg-red-50 text-red-700', icon: 'x' },
  revoked: { label: 'Revoked', tone: 'border-red-300 bg-red-50 text-red-700', icon: 'x' },
  pending: { label: 'Pending', tone: 'border-zinc-200 bg-white text-zinc-400', icon: 'dot' },
};

function documentNavigatorStatusMeta(item) {
  const status = getListStatus(item, 'document');
  return documentNavigatorStatus[status] || {
    label: String(status || 'pending').replaceAll('_', ' '),
    tone: 'border-zinc-200 bg-white text-zinc-400',
    icon: 'dot',
  };
}

function DocumentNavigatorStatus({ item }) {
  const meta = documentNavigatorStatusMeta(item);
  return (
    <span
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${meta.tone}`}
      title={meta.label}
      aria-label={meta.label}
    >
      {meta.icon === 'check' && <Check className="h-3 w-3" strokeWidth={2}/>}
      {meta.icon === 'x' && <X className="h-3 w-3" strokeWidth={2}/>}
      {meta.icon === 'dot' && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-75"/>}
    </span>
  );
}

function documentNavigatorDate(item) {
  const value = lastUpdatedTimestamp(item) || item.created_at;
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function SignatureDetailNavigator({ currentId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/signatures')
      .then(({ data }) => {
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load documents');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filteredItems = items.filter((item) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [
      item?.title,
      item?.signer_name,
      item?.signer_first_name,
      item?.signer_last_name,
      item?.display_name,
      item?.signer_email,
    ].join(' ').toLowerCase().includes(needle);
  });

  return (
    <aside className="hidden w-72 shrink-0 border-r border-zinc-100 bg-white lg:sticky lg:top-16 lg:flex lg:h-[calc(100vh-4rem)] lg:flex-col xl:w-80" data-testid="signature-detail-navigator">
      <div className="flex flex-col justify-center border-b border-zinc-100 bg-white px-3 py-3 lg:min-h-[104px]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate font-display text-lg font-semibold">Documents</h2>
            <p className="mt-0.5 truncate text-xs text-zinc-500">
              {loading ? 'Loading documents' : `${items.length} documents`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard/signatures/new')}
            title="New document"
            aria-label="New document"
            className="grid h-7 w-7 shrink-0 place-items-center border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.8}/>
          </button>
        </div>
        <div className="mt-3 flex h-8 items-center gap-2 border border-zinc-200 bg-white px-2.5">
          <Search className="h-4 w-4 text-zinc-400" strokeWidth={1.5}/>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-full min-w-0 flex-1 bg-transparent text-sm text-zinc-700 outline-none placeholder:text-zinc-400"
            placeholder="Search"
            aria-label="Search documents"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="px-3 py-8 text-sm text-zinc-500">Loading…</div>
        ) : error ? (
          <div className="px-3 py-8 text-sm text-zinc-500">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="px-3 py-8 text-sm text-zinc-500">
            {items.length === 0 ? 'No documents found.' : 'No matching documents.'}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredItems.map((item) => {
              const active = item.id === currentId;
              const signer = [item.signer_first_name, item.signer_last_name].filter(Boolean).join(' ')
                || item.signer_name || item.display_name || item.signer_email || 'Unnamed signer';
              const method = item.auth_method === 'kyc' || item.signature_level === 'AES' ? 'AES' : 'SES';
              const date = documentNavigatorDate(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { if (!active) navigate(`/dashboard/signatures/${item.id}`); }}
                  className={`w-full border px-3 py-2.5 text-left transition-colors ${active ? 'border-zinc-300 bg-white shadow-sm' : 'border-transparent bg-transparent hover:border-zinc-200 hover:bg-white'}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-zinc-900" title={item.title || 'Untitled document'}>{item.title || 'Untitled document'}</div>
                      <div className="mt-1 truncate text-xs text-zinc-500" title={signer}>{signer}</div>
                    </div>
                    {date && <div className="shrink-0 pt-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">{date}</div>}
                  </div>
                  <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-white px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
                        <FileSignature className="h-3 w-3" strokeWidth={1.8}/>{method}
                      </span>
                    </div>
                    <DocumentNavigatorStatus item={item}/>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}

function hasSigningActivity(status) {
  const normalized = String(status || '').toLowerCase();
  return ['signing_started', 'in_progress', 'processing'].includes(normalized);
}

function documentCheckState(status, { setupComplete = true, usesKyc = false, kycApproved = false, invitationSent = false, linkedVerificationUnavailable = false } = {}) {
  const normalized = String(status || 'draft').toLowerCase();
  if (['signed', 'completed', 'finalized'].includes(normalized)) return { tone: 'success' };
  if (isTerminalDocumentStatus(normalized)) return { tone: 'error' };
  if (usesKyc && linkedVerificationUnavailable) return { tone: 'error' };
  if (!setupComplete) return { tone: 'inactive' };
  if (usesKyc && !kycApproved) return { tone: 'inactive' };
  if (usesKyc) return { tone: 'neutral' };
  return { tone: invitationSent ? 'neutral' : 'inactive' };
}

function documentChecklistDescription(status, { setupComplete = true, usesKyc = false, kycApproved = false, invitationSent = false, linkedVerificationUnavailable = false } = {}) {
  const normalized = String(status || '').toLowerCase();
  if (['signed', 'completed', 'finalized'].includes(normalized)) return 'Document signing completed successfully.';
  if (normalized === 'revoked') return 'Document signing has been revoked.';
  if (normalized === 'expired') return 'Document signing has expired.';
  if (normalized === 'rejected') return 'Document signing was rejected.';
  if (normalized === 'failed') return 'Document signing failed.';
  if (usesKyc && linkedVerificationUnavailable) return 'Document signing can no longer be completed.';
  if (!setupComplete) return 'Document signing has not started yet.';
  if (usesKyc && !kycApproved) return 'Document signing has not started yet.';
  if (usesKyc && kycApproved) return 'Document signing is in progress.';
  if (!usesKyc && invitationSent) return 'Document signing is in progress.';
  if (hasSigningActivity(normalized)) return 'Document signing is in progress.';
  return 'Document signing has not started yet.';
}

function linkedVerificationState(status, setupComplete = true, { workflowTerminal = false } = {}) {
  const normalized = String(status || 'pending').toLowerCase();
  return {
    tone: normalizeChecklistTone({
      complete: ['approved', 'completed'].includes(normalized),
      active: setupComplete && !workflowTerminal,
      terminal: workflowTerminal,
      failed: ['rejected', 'revoked', 'expired', 'failed'].includes(normalized),
    }),
  };
}

function linkedVerificationChecklistDescription(status, setupComplete = true, { workflowTerminal = false } = {}) {
  const normalized = String(status || '').toLowerCase();
  if (['approved', 'completed'].includes(normalized)) return 'Linked KYC verification completed successfully.';
  if (normalized === 'rejected') return 'Linked KYC verification was rejected.';
  if (normalized === 'revoked') return 'Linked KYC verification has been revoked.';
  if (normalized === 'expired') return 'Linked KYC verification has expired.';
  if (normalized === 'failed') return 'Linked KYC verification failed.';
  if (workflowTerminal) return 'Linked KYC verification was not completed before this document flow ended.';
  if (!setupComplete || !normalized || ['not_started', 'draft', 'created'].includes(normalized)) return 'Linked KYC verification has not started yet.';
  if (['pending', 'opened', 'in_progress', 'processing', 'needs_review', 'needs_action'].includes(normalized)) return 'Linked KYC verification is in progress.';
  return 'Linked KYC verification is in progress.';
}

function setupCheckState(signature, fieldsCount, invitationSent) {
  const normalized = String(signature.status || '').toLowerCase();
  return {
    tone: normalizeChecklistTone({
      complete: Number(fieldsCount || 0) > 0 && invitationSent,
      active: true,
      terminal: isTerminalDocumentStatus(normalized),
    }),
  };
}

function setupChecklistDescription(signature, fieldsCount, invitationSent) {
  if (Number(fieldsCount || 0) > 0 && invitationSent) return 'Document setup completed successfully.';
  if (isTerminalDocumentStatus(signature.status)) return 'Document setup was not completed before this document flow ended.';
  if (Number(fieldsCount || 0) <= 0) return 'Add at least one signature field before sending this document.';
  if (!invitationSent) return 'Send the signing invitation to start the signing flow.';
  return 'Document setup completed successfully.';
}

function resolveSignatureBannerState(signature, { setupComplete = false, usesKyc = false, linkedKycApproved = false } = {}) {
  const docStatus = String(signature.status || '').toLowerCase();
  const kycStatus = String(signature.verification_status || '').toLowerCase();
  const signed = ['signed', 'completed', 'finalized'].includes(docStatus);

  if (usesKyc && ['revoked', 'expired'].includes(kycStatus)) {
    return {
      key: 'linked_verification_unavailable',
      tone: 'error',
      primary: 'This document signing flow can no longer be completed.',
      secondary: 'The linked KYC verification is no longer active.',
    };
  }

  if (docStatus === 'revoked') {
    return {
      key: 'document_revoked',
      tone: 'error',
      primary: 'This document signing flow has been revoked.',
      secondary: 'The signer can no longer access or sign this document.',
    };
  }

  if (docStatus === 'expired') {
    return {
      key: 'document_expired',
      tone: 'error',
      primary: 'This document has expired.',
      secondary: 'The signer can no longer access or sign this document.',
    };
  }

  if (signed) {
    return {
      key: 'signed',
      tone: 'success',
      primary: 'This document has been signed successfully.',
      secondary: 'The signing flow has been completed.',
    };
  }

  if (usesKyc && setupComplete && linkedKycApproved) {
    return {
      key: 'aes_ready_for_signing',
      tone: 'neutral',
      primary: 'Identity verification has been completed.',
      secondary: 'Waiting for the signer to complete the document.',
    };
  }

  if (usesKyc && setupComplete) {
    return {
      key: 'aes_waiting_for_kyc',
      tone: 'neutral',
      primary: 'Document signing is pending.',
      secondary: 'Waiting for the required verification or signer action.',
    };
  }

  if (!usesKyc && setupComplete) {
    return {
      key: 'ses_invitation_sent',
      tone: 'neutral',
      primary: 'This document has been sent for signature.',
      secondary: 'The signer can now review and sign the document.',
    };
  }

  return {
    key: 'setup_incomplete',
    tone: 'warning',
    primary: 'This document setup is incomplete.',
    secondary: 'Add required signature fields and send the signing invitation to start the signing flow.',
  };
}

const checklistMessageStyles = {
  success: 'border-emerald-300 bg-emerald-50 text-emerald-700 [&_.secondary]:text-emerald-700',
  neutral: 'border-zinc-200 bg-zinc-50 text-zinc-700 [&_.secondary]:text-zinc-600',
  warning: 'border-amber-300 bg-amber-50 text-amber-800 [&_.secondary]:text-amber-800',
  error: 'border-red-300 bg-red-50 text-red-700 [&_.secondary]:text-red-700',
};

function ChecklistMessage({ message }) {
  if (!message) return null;
  return (
    <div className={`border px-4 py-3 text-sm ${checklistMessageStyles[message.tone] || checklistMessageStyles.warning}`}>
      <div className="font-medium">{message.primary}</div>
      <div className="secondary mt-1">{message.secondary}</div>
    </div>
  );
}

function isInternalReference(value) {
  const reference = String(value || '').trim();
  if (!reference) return false;
  return [
    /^signature:[0-9a-f-]{36}$/i,
    /^verification:[0-9a-f-]{36}$/i,
    /^session:[0-9a-f-]{36}$/i,
  ].some((pattern) => pattern.test(reference));
}

function displayReference(value) {
  if (!value || isInternalReference(value)) return null;
  return value;
}

function LinkedVerificationValue({ person, verificationId }) {
  const action = verificationId ? (
    <Link
      to={`/dashboard/verifications/${verificationId}`}
      className="inline-flex h-8 shrink-0 items-center gap-2 border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 hover:border-zinc-400"
    >
      Open verification
    </Link>
  ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="min-w-0 break-words text-sm text-zinc-900">{person || 'KYC verification'}</div>
        <div className="mt-1 text-xs text-zinc-500">Related KYC verification</div>
      </div>
      {action}
    </div>
  );
}

function LinkedReferenceBox({ label, children }) {
  if (!children) return null;

  return (
    <div className="mt-3 rounded-lg border border-dashed border-zinc-300 bg-white p-4 transition-colors">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-3 min-w-0">{children}</div>
    </div>
  );
}

function documentHeaderStatusLabel(status) {
  const normalized = String(status || 'pending').toLowerCase();
  if (['signed', 'completed'].includes(normalized)) return 'Signed';
  if (['revoked', 'expired'].includes(normalized)) return normalized;
  if (['rejected', 'failed'].includes(normalized)) return 'Rejected';
  if (['setup_required', 'ready_to_send'].includes(normalized)) return 'Action required';
  return 'Pending';
}

export default function SignatureDetail() {
  const { signatureId } = useParams();
  const [signature, setSignature] = useState(null);
  const [fields, setFields] = useState([]);
  const [copied, setCopied] = useState(false);
  const [verifyCopied, setVerifyCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [, setEmailMessage] = useState(null);
  const [emailSubjectDraft, setEmailSubjectDraft] = useState('');
  const [emailBodyDraft, setEmailBodyDraft] = useState('');
  const [dragState, setDragState] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [summaryHeight, setSummaryHeight] = useState(null);
  const emailBodyRef = useRef(null);
  const summaryCardRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/signatures/${signatureId}`);
    setSignature(data.signature);
    setFields(data.signature.signature_fields || []);
  }, [signatureId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const node = summaryCardRef.current;
    if (!node) return undefined;

    const measure = () => setSummaryHeight(node.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [signature, fields.length, auditExpanded]);

  useEffect(() => {
    if (!signature) return;
    setEmailSubjectDraft(signature.email_subject || '');
    setEmailBodyDraft(signature.email_message || '');
  }, [signature]);

  useEffect(() => {
    const textarea = emailBodyRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [emailBodyDraft]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(Math.max(page, 1), pageCount || 1));
  }, [pageCount]);

  useEffect(() => {
    if (!signature) return undefined;
    let objectUrl;
    const path = signature.status === 'signed'
      ? `/signatures/${signature.id}/signed-document`
      : `/signatures/${signature.id}/document`;
    api.get(path, { responseType: 'blob' })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        setPdfUrl(objectUrl);
      })
      .catch((e) => {
        console.error('[signatures] document preview failed', e?.response?.data || e);
        setPdfUrl(null);
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [signature]);

  const auditEvents = useMemo(() => {
    if (!signature) return [];
    const storedEvents = Array.isArray(signature.audit_events) ? signature.audit_events.map((event) => ({
      type: event.type,
      at: event.at,
      summary: event.type?.replaceAll('_', ' ').replaceAll('.', ' '),
      payload: event.payload || {},
    })) : [];
    const fallbackItems = [
      {
        type: 'document.created',
        at: signature.created_at,
        summary: `Document "${signature.title}" was created.`,
        payload: {
          title: signature.title,
          status: signature.status,
          document_hash: signature.document_hash,
          signature_level: signature.signature_level,
          auth_method: signature.auth_method,
        },
      },
      {
        type: signature.auth_method === 'kyc' ? 'verification.required' : 'verification.not_required',
        at: signature.created_at,
        summary: signature.auth_method === 'kyc'
          ? 'Signer must complete KYC before signing.'
          : 'No KYC verification required.',
        payload: {
          signature_level: signature.signature_level,
          auth_method: signature.auth_method,
          status: signature.verification_status || 'not_started',
        },
      },
      {
        type: 'link.created',
        at: signature.created_at,
        summary: 'Public signing link created.',
        payload: {
          verification_id: signature.verification_id,
          signature_level: signature.signature_level,
          auth_method: signature.auth_method,
        },
      },
    ];
    const items = storedEvents.length ? storedEvents : fallbackItems;
    const hasEvent = (type) => items.some((event) => event.type === type);
    if (fields.length > 0 && !hasEvent('document.fields_saved')) {
      items.push({
        type: 'document.fields_saved',
        at: signature.updated_at || signature.created_at,
        summary: `${fields.length} signature field(s) configured.`,
        payload: {
          status: signature.status,
          signature_fields_count: fields.length,
          signature_level: signature.signature_level,
          auth_method: signature.auth_method,
        },
      });
    }
    if (signature.signed_at && !hasEvent('document.signed')) {
      items.push({
        type: 'document.signed',
        at: signature.signed_at,
        summary: 'Document signed.',
        payload: {
          status: 'signed',
          signed_at: signature.signed_at,
          verification_id: signature.verification_id,
          document_hash: signature.document_hash,
          signature_level: signature.signature_level,
          auth_method: signature.auth_method,
        },
      });
    }
    if (signature.signed_document_hash && !hasEvent('document.finalized')) {
      items.push({
        type: 'document.finalized',
        at: signature.signed_at,
        summary: 'Signed PDF finalized.',
        payload: {
          signed_document_hash: signature.signed_document_hash,
          verification_id: signature.verification_id,
          signature_level: signature.signature_level,
          auth_method: signature.auth_method,
        },
      });
    }
    return items.filter((event) => !(event.type?.startsWith('usage.') && event.payload?.resource_type === 'verification'));
  }, [signature, fields.length]);

  const copy = () => {
    if (!signature.setup_complete) return;
    navigator.clipboard.writeText(signature.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const copyVerify = () => {
    if (!signature.verification_url) return;
    navigator.clipboard.writeText(signature.verification_url);
    setVerifyCopied(true);
    setTimeout(() => setVerifyCopied(false), 1500);
  };

  const downloadSignedPdf = async () => {
    const { data } = await api.get(`/signatures/${signature.id}/signed-document`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${signature.title || 'signed-document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const sendInvitation = async () => {
    setResending(true);
    setEmailMessage(null);
    try {
      const { data } = await api.post(`/signatures/${signature.id}/resend-invitation`, {
        email_subject: emailSubjectDraft,
        email_message: emailBodyDraft,
      });
      setSignature(data.signature);
      setEmailMessage(data.signature.email_warning || 'Invitation sent.');
    } catch (e) {
      console.error('[signatures] resend invitation failed', e?.response?.data || e);
      setEmailMessage(e?.response?.data?.error === 'signature_setup_required'
        ? 'Add at least one signature field before sending this document.'
        : e?.response?.data?.error === 'invitation_already_sent'
          ? 'Invitation has already been sent.'
          : 'Could not send invitation.');
    } finally {
      setResending(false);
    }
  };

  const addField = () => {
    setFields((current) => [
      ...current,
      { type: 'signature', page: currentPage, x: 0.18, y: 0.68, width: 0.34, height: 0.09, required: true },
    ]);
  };

  const updateField = (index, patch) => {
    setFields((current) => current.map((field, i) => i === index ? { ...field, ...patch } : field));
  };

  const deleteField = (index) => {
    setFields((current) => current.filter((_, i) => i !== index));
  };

  const saveFields = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      const { data } = await api.post(`/signatures/${signatureId}/fields`, { signature_fields: fields });
      setSignature(data.signature);
      setFields(data.signature.signature_fields || []);
      setSaveMessage('Signature fields saved. Review the email and send the invitation when you are ready.');
    } catch (e) {
      console.error('[signatures] save fields failed', e?.response?.data || e);
      setSaveError('Could not save signature fields. Check placement and try again.');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    try {
      await api.post(`/signatures/${signature.id}/revoke`);
      setRevokeOpen(false);
      await load();
    } catch (e) {
      console.error('[signatures] revoke failed', e?.response?.data || e);
      setSaveError('Could not revoke this link.');
    }
  };

  const startPointer = (event, index, mode) => {
    const box = event.currentTarget.closest('[data-editor-surface]').getBoundingClientRect();
    const field = fields[index];
    event.preventDefault();
    setDragState({
      index,
      mode,
      box,
      startX: event.clientX,
      startY: event.clientY,
      field,
    });
  };

  useEffect(() => {
    if (!dragState) return undefined;
    const move = (event) => {
      const dx = (event.clientX - dragState.startX) / dragState.box.width;
      const dy = (event.clientY - dragState.startY) / dragState.box.height;
      const base = dragState.field;
      if (dragState.mode === 'move') {
        const x = Math.min(Math.max(base.x + dx, 0), 1 - base.width);
        const y = Math.min(Math.max(base.y + dy, 0), 1 - base.height);
        updateField(dragState.index, { x, y });
      } else {
        const width = Math.min(Math.max(base.width + dx, 0.06), 1 - base.x);
        const height = Math.min(Math.max(base.height + dy, 0.04), 1 - base.y);
        updateField(dragState.index, { width, height });
      }
    };
    const up = () => setDragState(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragState]);

  if (!signature) return <div className="text-sm text-zinc-500">Loading...</div>;
  const isRevoked = signature.status === 'revoked';
  const isExpired = signature.status === 'expired';
  const workflowTerminal = isTerminalDocumentStatus(signature.status);
  const isTerminalUnavailable = workflowTerminal;
  const linkReady = !!signature.setup_complete;
  const isSigned = signature.status === 'signed';
  const hasSignatureEvidence = isSigned;
  const sentStatuses = ['sent', 'opened', 'signing_started', 'signed', 'completed'];
  const lockedStatuses = ['sent', 'opened', 'signing_started', 'signed', 'completed', 'revoked', 'expired', 'rejected'];
  const isSent = !!signature.email_sent_at || sentStatuses.includes(signature.status);
  const fieldsLocked = !!signature.email_sent_at || lockedStatuses.includes(signature.status);
  const linkUsable = linkReady && isSent && !workflowTerminal;
  const canRevoke = !signature.usage_used_at && !['signed', 'completed', 'finalized'].includes(signature.status) && !workflowTerminal;
  const usesKyc = signature.auth_method === 'kyc' || signature.signature_level === 'AES';
  const signatureLevel = signature.signature_level === 'SES'
    ? 'Simple Electronic Signature (SES)'
    : 'Advanced Electronic Signature (AES)';
  const authMethod = usesKyc ? 'KYC verification' : 'Mail verification';
  const displayStatus = isRevoked
    ? 'revoked'
    : isExpired
      ? 'expired'
      : signature.status === 'rejected'
        ? 'rejected'
        : signature.setup_complete
          ? signature.status
          : 'setup_required';
  const verificationBadge = verificationBadgeForSignature(signature);
  const documentTitle = String(signature.title || '').trim();
  const displayStatusLabel = documentHeaderStatusLabel(displayStatus);
  const reportReference = displayReference(signature.reference);
  const hasReportReference = hasValue(reportReference);
  const signedAtLabel = signature.signed_at
    ? new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).format(new Date(signature.signed_at))
    : null;
  const linkedVerificationAvailable = usesKyc && (
    signature.verification_session_id || signature.verification_name || signature.verification_email
  );
  const invitationSent = isSent || auditEvents.some((event) => event.type === 'email.invitation_sent');
  const documentSetupComplete = fields.length > 0 && invitationSent;
  const linkedKycApproved = ['approved', 'completed'].includes(String(signature.verification_status || '').toLowerCase());
  const linkedVerificationUnavailable = usesKyc && ['revoked', 'expired'].includes(String(signature.verification_status || '').toLowerCase());
  const documentChecklistContext = {
    setupComplete: documentSetupComplete,
    usesKyc,
    kycApproved: linkedKycApproved,
    invitationSent,
    linkedVerificationUnavailable,
  };
  const setupCheck = setupCheckState(signature, fields.length, invitationSent);
  const linkedVerificationCheck = linkedVerificationState(signature.verification_status, documentSetupComplete, { workflowTerminal });
  const documentCheck = documentCheckState(displayStatus, documentChecklistContext);
  const workflowSteps = [
    {
      key: 'setup',
      label: 'Setup',
      description: setupChecklistDescription(signature, fields.length, invitationSent),
      tone: mainWorkflowTone(setupCheck.tone),
      icon: Settings2,
      substeps: [
        ...(usesKyc ? [{
          key: 'linked-verification',
          label: 'Linked verification',
          tone: linkedVerificationCheck.tone,
          icon: UserCheck,
          linked: true,
        }] : []),
        {
          key: 'signature',
          label: 'Signature',
          tone: documentCheck.tone,
          icon: PenLine,
        },
      ],
    },
    {
      key: 'document',
      label: 'Document',
      description: documentChecklistDescription(displayStatus, documentChecklistContext),
      tone: mainWorkflowTone(documentCheck.tone),
      icon: FileSignature,
    },
  ];
  const bannerState = resolveSignatureBannerState(signature, {
    setupComplete: documentSetupComplete,
    usesKyc,
    linkedKycApproved,
  });

  return (
    <div data-testid="signature-detail" className="lg:-mx-8 lg:-my-8 lg:flex lg:min-h-[calc(100vh-4rem)]">
      <SignatureDetailNavigator currentId={signatureId}/>
      <div className="min-w-0 flex-1 lg:px-8 lg:py-8">
      <VerificationBreadcrumb
        rootLabel="Documents"
        rootTo="/dashboard/signatures"
        current={documentTitle || 'Document details'}
        showDashboard={false}
      />

      <div className="mb-6 bg-white lg:sticky lg:top-16 lg:z-20 lg:-mx-8 lg:-mt-8 lg:border-b lg:border-zinc-100 lg:bg-white/85 lg:px-8 lg:pb-3 lg:pt-8 lg:backdrop-blur-md supports-[backdrop-filter]:lg:bg-white/75">
        <div className="flex min-w-0 flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Document</p>
            <h1 className="mt-1 flex min-w-0 items-start gap-2 font-display text-3xl font-bold tracking-tight">
              <FileText className="mt-1 h-6 w-6 shrink-0 text-zinc-400" strokeWidth={1.5}/>
              <span className="min-w-0 break-words">{fmt(signature.title)}</span>
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <SignatureBadge status={displayStatus} displayStatus={displayStatusLabel}/>
              <LinkedVerificationBadge badge={verificationBadge} verificationId={signature.verification_session_id}/>
              {signature.status === 'draft' && <span className="text-xs font-mono text-zinc-500">Saved as draft</span>}
              <span className="inline-flex min-w-0 items-center border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                <span className="min-w-0 break-all">ID: {signature.id}</span>
              </span>
            </div>
          </div>

          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <a href={linkUsable ? signature.public_url : undefined} target="_blank" rel="noreferrer"
               aria-disabled={!linkUsable}
               className={`inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 ${linkUsable ? 'hover:border-zinc-400' : 'opacity-40 pointer-events-none'}`}>
              <ExternalLink className="w-4 h-4" strokeWidth={1.5}/> Open public link
            </a>
            <button onClick={copy} disabled={!linkUsable}
              className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400 disabled:opacity-40 disabled:hover:border-zinc-200">
              {copied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>} Copy URL
            </button>
            {canRevoke && (
              <button onClick={() => setRevokeOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm border border-red-200 text-red-700 rounded-sm px-3 py-2 hover:border-red-400">
                <Ban className="w-4 h-4" strokeWidth={1.5}/> Revoke
              </button>
            )}
            {isSigned && (
              <button onClick={downloadSignedPdf}
                className="inline-flex items-center gap-1.5 text-sm bg-black text-white rounded-sm px-3 py-2 hover:bg-zinc-800">
                <Download className="w-4 h-4" strokeWidth={1.5}/> Download signed PDF
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <ChecklistMessage message={bannerState}/>
      </div>

      {!isTerminalUnavailable && !isSigned && !isSent && (
        <section className="mb-6 border border-zinc-200 rounded-sm p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-semibold">Send invitation</h2>
              <p className="text-sm text-zinc-500 mt-1">
                Review the email and send it after at least one signature field is saved.
              </p>
            </div>
            <button onClick={sendInvitation} disabled={resending || !linkReady || !emailSubjectDraft.trim() || !emailBodyDraft.trim()}
              className="inline-flex items-center gap-1.5 text-sm bg-black text-white rounded-sm px-3 py-2 hover:bg-zinc-800 disabled:opacity-40">
              <Mail className="w-4 h-4" strokeWidth={1.5}/> {resending ? 'Sending...' : 'Send invitation'}
            </button>
          </div>
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Email subject</label>
          <input value={emailSubjectDraft} onChange={(e) => setEmailSubjectDraft(e.target.value)}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Email message</label>
          <textarea ref={emailBodyRef} value={emailBodyDraft} onChange={(e) => setEmailBodyDraft(e.target.value)}
            rows={6}
            className="w-full min-h-40 overflow-y-hidden resize-none border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
          <p className="mt-2 text-xs text-zinc-500 font-mono">The secure signing link is added to the email automatically. Message is sent as plain text inside the email template.</p>
        </section>
      )}

      <div className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] lg:items-start">
          <div ref={summaryCardRef} className="min-w-0 space-y-6">
            <section className="border border-zinc-200 bg-white p-6">
              <h2 className="font-display text-lg font-semibold">Document summary</h2>
              <div className="mt-3">
                <Field label="Document title" value={signature.title}/>
                <Field label="Signer" value={signature.signer_name || signature.display_name}/>
                <Field label="Email" value={signature.signer_email} mono/>
                <Field label="Signature method" value={signatureLevel} noDivider={linkedVerificationAvailable && !hasReportReference}/>
                <Field label="Reference" value={reportReference} mono hideEmpty noDivider={linkedVerificationAvailable && hasReportReference}/>
                {linkedVerificationAvailable && (
                  <LinkedReferenceBox label="Linked verification">
                    <LinkedVerificationValue
                      person={signature.verification_name || signature.verification_email}
                      verificationId={signature.verification_session_id}
                    />
                  </LinkedReferenceBox>
                )}
              </div>
            </section>
            <DocumentWorkflowCard steps={workflowSteps}/>
          </div>

          <AuditTrail
            events={auditEvents}
            className="hidden h-full lg:flex"
            previewHeight={summaryHeight}
            expanded={auditExpanded}
            onExpand={() => setAuditExpanded(true)}
            onCollapse={() => setAuditExpanded(false)}
          />
        </div>

        {hasSignatureEvidence && (
          <section className="border border-zinc-200 rounded-sm bg-white p-6">
            <h2 className="font-display text-lg font-semibold">Signature details</h2>
            <div className="mt-3">
              <Field label="Signed name" value={signature.signed_name} hideEmpty/>
              <Field label="Signed at" value={signedAtLabel} mono hideEmpty/>
              <Field label="Signature fields" value={`${fields.length}`} mono/>
              <Field label="Auth method" value={authMethod}/>
            </div>
          </section>
        )}
      </div>

      <section className="mt-6 border border-zinc-200 rounded-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-lg font-semibold">{isSigned ? 'Signed document' : 'Signature placement'}</h2>
            <p className="text-sm text-zinc-500 mt-1">
              {isSigned ? 'The finalized signed PDF is locked for audit.' : 'Place typed-signature fields on the uploaded PDF.'}
            </p>
          </div>
          {isSigned ? (
            <div className="flex items-center gap-2">
              <a href={signature.verification_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400">
                <ExternalLink className="w-4 h-4" strokeWidth={1.5}/> Verify authenticity
              </a>
              <button onClick={copyVerify}
                className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400">
                {verifyCopied ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>} Copy verify link
              </button>
            </div>
          ) : fieldsLocked ? (
            <div className="text-sm text-zinc-500 font-mono">Fields locked after sending.</div>
          ) : (
          <div className="flex items-center gap-2">
            <button onClick={addField}
              className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-400">
              <Plus className="w-4 h-4" strokeWidth={1.5}/> Add signature field
            </button>
            <button onClick={saveFields} disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm bg-black text-white rounded-sm px-3 py-2 hover:bg-zinc-800 disabled:opacity-50">
              <Save className="w-4 h-4" strokeWidth={1.5}/> {saving ? 'Saving...' : 'Save fields'}
            </button>
          </div>
          )}
        </div>

        <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
          <div className="h-11 px-3 border-b border-zinc-200 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
              disabled={currentPage <= 1}
              className="text-xs font-mono border border-zinc-200 rounded-sm px-2 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs font-mono text-zinc-500">page {currentPage} / {pageCount}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(page + 1, pageCount))}
              disabled={currentPage >= pageCount}
              className="text-xs font-mono border border-zinc-200 rounded-sm px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
          <PdfCanvasViewer
            pdfUrl={pdfUrl}
            currentPage={currentPage}
            onPageCount={setPageCount}
            scrollable={false}
            surfaceProps={{ 'data-editor-surface': true }}
          >
            {!isSigned && fields.map((field, index) => field.page === currentPage ? (
              <div key={index}
                onPointerDown={fieldsLocked ? undefined : (event) => startPointer(event, index, 'move')}
                className={`absolute border-2 border-dashed ${fieldsLocked ? 'border-zinc-500 bg-zinc-100/40 cursor-default' : 'border-blue-600 bg-blue-100/40 cursor-move'}`}
                style={{
                  left: `${field.x * 100}%`,
                  top: `${field.y * 100}%`,
                  width: `${field.width * 100}%`,
                  height: `${field.height * 100}%`,
                }}>
                <div className={`flex items-center justify-between gap-2 px-2 py-1 text-[11px] font-mono ${fieldsLocked ? 'text-zinc-700 bg-zinc-50/90' : 'text-blue-900 bg-blue-50/90'}`}>
                  <span>signature</span>
                  {!fieldsLocked && (
                    <button onPointerDown={(event) => event.stopPropagation()} onClick={() => deleteField(index)}
                      className="text-blue-900 hover:text-red-700" aria-label="Delete signature field">
                      <Trash2 className="w-3 h-3" strokeWidth={1.8}/>
                    </button>
                  )}
                </div>
                {!fieldsLocked && (
                  <button
                    type="button"
                    aria-label="Resize signature field"
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      startPointer(event, index, 'resize');
                    }}
                    className="absolute right-0 bottom-0 w-4 h-4 translate-x-1/2 translate-y-1/2 border border-blue-700 bg-white cursor-se-resize"
                  />
                )}
              </div>
            ) : null)}
          </PdfCanvasViewer>
        </div>
        {saveMessage && <p className="mt-3 text-sm text-green-700">{saveMessage}</p>}
        {saveError && <p className="mt-3 text-sm text-red-700">{saveError}</p>}
      </section>

      <AuditTrail events={auditEvents} className="mt-6 flex lg:hidden"/>

      {revokeOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onClick={() => setRevokeOpen(false)}>
          <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-sm p-6" onClick={(event) => event.stopPropagation()}>
            <h2 className="font-display text-xl font-semibold">Revoke link?</h2>
            <p className="text-sm text-zinc-600 mt-2">This action cannot be undone. The recipient will no longer be able to access this link.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setRevokeOpen(false)}
                className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
                Cancel
              </button>
              <button onClick={revoke}
                className="text-sm px-3 py-2 bg-red-700 text-white rounded-sm hover:bg-red-800">
                Revoke link
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
